/**
 * Signup Page Component
 * 
 * Handles user registration flow including:
 * - User registration form with validation
 * - Email verification process
 * - Automatic login after successful verification
 * 
 * Integrates with AWS Cognito User Pool for user management.
 */

import * as React from "react";
import {
  CognitoUserAttribute,
  CognitoUserPool,
} from "amazon-cognito-identity-js";
import { AuthContext } from "../../context/auth-context";
import { useNavigate } from "react-router-dom";
import { Paper, Stack, Typography } from "@mui/material";
import SignupForm from "./components/signup-form";
import SignupVerificationForm from "./components/signup-verification-form";
import { ClearCallLogoTitle } from "../../components/clear-call-logo";
import Copyright from "../../components/copyright";
import ribbonBackground from "../../assets/ribbon.png";

import { v4 as uuidv4 } from 'uuid';
import { RouteType } from "../../router";

// Environment variables for AWS Cognito configuration
const envUserPoolId = import.meta.env.VITE_USER_POOL_ID as string;
const envClientId = import.meta.env.VITE_CLIENT_ID as string;

// AWS Cognito User Pool instance
const userPool: CognitoUserPool = new CognitoUserPool({
  UserPoolId: envUserPoolId,
  ClientId: envClientId,
});

const SignupPage: React.FC = () => {
  // State for managing signup flow
  const [signupResult, setSignupResult] = React.useState<any>(null);
  const [email, setEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");

  const { authenticateUser } = React.useContext(AuthContext);
  const navigate = useNavigate();

  /**
   * Updates user confirmation status and triggers re-render
   * @param confirmed - Whether the user has been confirmed
   */
  const userConfirmed = (confirmed: boolean) => {
    setSignupResult({ ...signupResult, userConfirmed: confirmed });
    console.log("User confirmed");
  };

  /**
   * Effect to automatically log in user after email verification
   */
  React.useEffect(() => {
    if (!signupResult) {
      return;
    }

    if (!signupResult.userConfirmed) {
      return;
    }

    // Automatically authenticate user after successful verification
    authenticateUser(email, password, {
      onSuccess: () => {
        navigate(RouteType.Calendar);
      },
      onFailure: (err: unknown) => {
        console.error("Auto-login failed:", err);
      },
    });
  }, [signupResult, email, password, authenticateUser, navigate]);

  /**
   * Handles user registration with AWS Cognito
   * @param fullName - User's full name
   * @param email - User's email address
   * @param password - User's password
   * @param errorCallback - Callback to handle registration errors
   */
  const signUp = (fullName: string, email: string, password: string, errorCallback: (error: string) => void) => {
    // Define user attributes for Cognito
    const userAttributes = [
      new CognitoUserAttribute({ Name: "preferred_username", Value: fullName }),
      new CognitoUserAttribute({ Name: "custom:userId", Value: uuidv4() }),
    ];

    userPool.signUp(
      email,
      password,
      userAttributes,
      [],
      (err, result) => {
        if (err) {
          console.error("Signup error:", err, typeof err, err.name, err.message);
          if (err.name === "UsernameExistsException") {
            errorCallback("User already exists");
          } else {
            errorCallback(err.message || "Registration failed");
          }
          return;
        }

        console.log("Signup successful:", result);
        setEmail(email);
        setPassword(password);
        setSignupResult(result);
      }
    );
  };

  /**
   * Confirms user registration with verification code
   * @param verificationCode - Email verification code
   * @param errorCallback - Callback to handle confirmation errors
   */
  const confirmUser = (verificationCode: string, errorCallback: (error: string) => void) => {
    if (!signupResult) {
      return;
    }

    signupResult?.user.confirmRegistration(verificationCode, true, (err: { code: string; message: string; }, result: any) => {
      if (err) {
        // Handle different error types with user-friendly messages
        if (err.code === "ExpiredCodeException") {
          errorCallback("Code expired. Please request a new one.");
          return;
        }

        if (err.code === "CodeMismatchException") {
          errorCallback("Invalid code. Please try again.");
          return;
        }

        if (err.code === "NotAuthorizedException") {
          if (err.message === "User cannot be confirmed. Current status is CONFIRMED") {
            userConfirmed(true);
            return;
          }
          errorCallback("Invalid code. Please try again.");
          return;
        }

        if (err.code === "UserNotFoundException") {
          errorCallback("Invalid code. Please try again.");
          return;
        }

        errorCallback(err.message);
        console.error("Confirmation error:", err);
        return;
      }

      userConfirmed(true);
    });
  };

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={{
        minHeight: "100vh",
        backgroundImage:
          `linear-gradient(160deg, rgba(0, 99, 102, 0.73), rgba(0, 76, 131, 0.93)), url(${ribbonBackground})`,
        backgroundBlendMode: "normal",
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Paper
        elevation={20}
        sx={{
          width: 400,
          p: 6,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 3,
          background:
            "linear-gradient(145deg, rgba(255, 255, 255, 0.64) 0%, rgba(255, 255, 255, 0.28) 100%)",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          boxShadow:
            "0 24px 60px rgba(8, 39, 69, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.35)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
      >
        <ClearCallLogoTitle size={150} />
        <Typography variant="h3" sx={{ mb: 3 }}>
          Sign Up
        </Typography>
        {signupResult ? (
          <SignupVerificationForm
            confirmUser={confirmUser}
            codeDeliveryDetails={signupResult?.codeDeliveryDetails}
          />
        ) : (
          <SignupForm
            signUp={signUp}
          />
        )}
        <Copyright color="rgba(255, 255, 255, 0.7)" sx={{ mt: 5 }} />
      </Paper>
    </Stack>
  );
};

export default SignupPage;